import { AR, SSD } from "../../outboundAmzl/util/Constants";
import {
  BEDTIME,
  BREAKFAST,
  BRUNCH,
  COFFEE,
  DINNER,
  LUNCH,
  NIGHTOWL,
  ONE_HOUR_DELIVERY,
  SUNRISE,
  SUPPER,
  TWILIGHT,
} from "./Constants";
import {
  ASSIGNABLE,
  DEPARTED,
  INTERIM_STAGED,
  NOT_READY,
  PENDING_DEPART,
  PENDING_PICK,
  PENDING_STAGE,
} from "../../outboundAmzl/RouteStatusManager";
import Box from "@amzn/meridian/box";
import React from "react";

const boxWidth = 16;
const boxHeight = 16;
const partialIconSrc =
  "data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4NCjxzdmcgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB2aWV3Qm94PSIwIDAgMjIgMjIiPjxjaXJjbGUgY3g9IjExIiBjeT0iLTEwNDEuMzYiIHI9IjgiIHRyYW5zZm9ybT0ibWF0cml4KDEgMCAwLTEgMC0xMDMwLjM2KSIgb3BhY2l0eT0iLjk4IiBmaWxsPSIjZGE0NDUzIi8+PHBhdGggZD0ibS0yNi4zMDkgMTguMDdjLTEuMTggMC0yLjEzNS45NjgtMi4xMzUgMi4xMjl2MTIuODJjMCAxLjE3Ni45NDggMi4xMjkgMi4xMzUgMi4xMjkgMS4xODMgMCAyLjEzNS0uOTY4IDIuMTM1LTIuMTI5di0xMi44MmMwLTEuMTc2LS45NDYtMi4xMjktMi4xMzUtMi4xMjl6bTAgMjEuMzQ4Yy0xLjE4IDAtMi4xMzUuOTU0LTIuMTM1IDIuMTM1IDAgMS4xOC45NTQgMi4xMzUgMi4xMzUgMi4xMzUgMS4xODEgMCAyLjEzNS0uOTU0IDIuMTM1LTIuMTM1IDAtMS4xOC0uOTUyLTIuMTM1LTIuMTM1LTIuMTM1eiIgdHJhbnNmb3JtPSJtYXRyaXgoLjMwMDU2IDAgMCAuMzAwNTYgMTguOTAyIDEuNzI4KSIgZmlsbD0iI2ZmZiIgc3Ryb2tlPSIjZmZmIi8+PC9zdmc+";

/**
 * Check if a station is of program type AR
 * @param capability
 * @returns {*|boolean}
 */
export function isAR(capability) {
  return capability && capability === AR;
}

/**
 * Check if a station is of program type SSD
 * @param capability
 * @returns {*|boolean}
 */
export function isSSD(capability) {
  return capability && capability === SSD;
}

const deliveryWindowPriority = new Map();
deliveryWindowPriority.set(ONE_HOUR_DELIVERY, 0);
deliveryWindowPriority.set(NIGHTOWL, 1);
deliveryWindowPriority.set(SUNRISE, 2);
deliveryWindowPriority.set(COFFEE, 3);
deliveryWindowPriority.set(BREAKFAST, 4);
deliveryWindowPriority.set(BRUNCH, 5);
deliveryWindowPriority.set(LUNCH, 6);
deliveryWindowPriority.set(DINNER, 7);
deliveryWindowPriority.set(SUPPER, 8);
deliveryWindowPriority.set(BEDTIME, 9);
deliveryWindowPriority.set(TWILIGHT, 10);

export function getDeliveryWindowPriority(deliveryWindow) {
  return deliveryWindowPriority.has(deliveryWindow)
    ? deliveryWindowPriority.get(deliveryWindow)
    : 11;
}

export function getRouteColor(status) {
  switch (status) {
    case ASSIGNABLE:
      return "#BBC0C1";
    case NOT_READY:
    case PENDING_PICK:
      return "#FAD000";
    case PENDING_STAGE:
      return "#FF714D";
    case INTERIM_STAGED:
      return "#8169F8";
    case PENDING_DEPART:
      return "#47C2EC";
    case DEPARTED:
      return "#98C91E";
    default:
      return "#BBC0C1";
  }
}

const getPartialIcon = () => {
  return (
    <img
      className="partial-icon"
      alt={"partial"}
      src={partialIconSrc}
      width="16px"
      height="16px"
    />
  );
};

const getSquareBox = (color, isPartial, height, width) => {
  return (
    <Box
      minHeight={height}
      minWidth={width}
      maxHeight={height}
      maxWidth={width}
      backgroundColor={color}
    >
      {isPartial && getPartialIcon()}
    </Box>
  );
};

const getSquareBoxWithBoundary = (color, isPartial) => {
  return (
    <Box
      minHeight={boxHeight}
      minWidth={boxWidth}
      maxHeight={boxHeight}
      maxWidth={boxWidth}
      backgroundColor="#AD0A30"
      spacingInset="xxsmall"
    >
      {getSquareBox(color, isPartial, boxHeight - 4, boxWidth - 4)}
    </Box>
  );
};

export function renderRouteBox(color, isAtRisk, isPartial) {
  return !isAtRisk
    ? getSquareBox(color, isPartial, boxWidth, boxHeight)
    : getSquareBoxWithBoundary(color, isPartial);
}

const getToolTipTextFromList = (list) => {
  if (Array.isArray(list) && list.length) {
    return list.map((element, index) => {
      return (
        <span key={element.displayLabel}>
          {" "}
          <b>
            {element.displayLabel}
            {index !== list.length - 1 ? "," : ""}
          </b>
          <br />
        </span>
      );
    });
  }
  return "...";
};

export function getStagingLocation(route) {
  return (
    <span style={{ textAlign: "left" }}>
      Location: {getToolTipTextFromList(route.stagingLocations)}
    </span>
  );
}

export function getDisplayProgress(route) {
  switch (route.overviewStatus) {
    case PENDING_STAGE:
      return (
        <span>
          Stage Progress: <b>{route.stageProgress}</b>
          <br />
        </span>
      );
    case PENDING_DEPART:
    case DEPARTED:
      return (
        <span>
          Depart Progress: <b>{route.departProgress}</b>
          <br />
        </span>
      );
    default:
      return "";
  }
}



// WEBPACK FOOTER //
// ./src/components/outboundSSD/util/Util.js